import { CommonModule, Location } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EnvironmentInjector,
  inject,
  Inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ModalService } from '@indice/ng-components';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { catchError, EMPTY, Observable, tap } from 'rxjs';
import { distinct } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import {
  InsertOrderItemRequest,
  InsertOrderRequest,
  OrderResponse,
  ServicesPaymentsApiService,
} from 'src/app/core/services/payments-api-service';
import {
  UnikoServicesConfig,
  UnikoServiceTypes,
  UnikoServiceTypesString,
} from 'src/app/services/payments-api.service';
import {
  PortalApiService,
  PreApprovalStatus,
  PropertyDetails,
  PropertySummary,
  PropertyType,
} from 'src/app/services/portal-api.service';
import { ToasterService, ToastType } from 'src/app/services/toaster.service';
import { ExpertsCallbackComponent } from 'src/app/shared/components/experts-callback/experts-callback.component';
import { ReportTemplatePreviewModal } from 'src/app/shared/components/report-template-preview-modal/report-template-preview-modal.component';
import { GeneralCallbackModal } from 'src/app/shared/components/general-callback-modal/general-callback-modal.component';
import { PropertyCardItemComponent } from 'src/app/shared/components/property-card-item/property-card-item.component';
import { ServiceCardComponent } from 'src/app/shared/components/service-card/service-card.component';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';
import { ResponsiveService } from 'src/app/shared/services/responsive.service';
import { ThreedViewerComponent } from 'src/app/shared/threed-viewer/threed-viewer.component';
import { localizeRoute } from 'src/app/shared/utilities/localizeRoutes';
import { ServiceRequestUnauthorizedModalComponent } from '../service-request-unauthorized-modal/service-request-unauthorized-modal.component';
import { biddingSupportService } from './config/biddingSupport';
import { legalReportService } from './config/legalReport';
import { photographyService } from './config/photography';
import { preApprovalService } from './config/preapproval';
import { propertyFileConstitutionService } from './config/propertyFileConstitution';
import { technicalReportService } from './config/technicalReport';
import { CustomTooltipDirective } from 'src/app/shared/directives/custom-tooltip.directive';
import { CmsApiService, Offer } from 'src/app/services/cms-api.service';
import { FloatingBannerComponent } from '../../../shared/components/floating-banner/floating-banner.component';

@Component({
  selector: 'app-service-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PropertyCardItemComponent,
    TranslocoDirective,
    ServiceCardComponent,
    ExpertsCallbackComponent,
    LanguageRouterLinkDirective,
    CustomTooltipDirective,
    FloatingBannerComponent,
  ],
  templateUrl: './service-details.component.html',
  styleUrl: './service-details.component.css',
})
export class ServiceDetailsComponent implements OnDestroy {
  @ViewChild('asideContent') asideRef: ElementRef | undefined;
  public isOpen: boolean = false;
  public readonly deliveryDays: number = 8;
  public readonly isMobile$!: Observable<boolean>;
  public serviceData!: any;
  protected propertyCode: string | undefined;
  public serviceType!: UnikoServiceTypes;
  public readonly serviceTypeForTemplate = UnikoServiceTypes;
  public serviceTypeString = UnikoServiceTypesString;

  public readonly propertySummary: PropertySummary = new PropertySummary({
    id: '6fed1807-7cd3-414f-2caf-08dcf2a66877',
    code: '00000072',
    price: 113000,
    floor: 6,
    propertyArea: 245,
    description: 'Μοντέρνο διαμέρισμα σε ιδανική τοποθεσία Μαρουσίου',
    type: PropertyType.Maisonette,
    viewCount: 1,
    isSold: false,
    preApproved: PreApprovalStatus.Approved,
    defaultImagePath:
      'https://s1-dev.uniko.gr/dev-media/00000072/photos-primary.jpg',
    isPromoted: false,
    isFavorite: false,
    isHot: false,
    isSponsored: false,
    tags: [],
    title: 'Μεζονέτα, Δημοτική Κοινότητα Ευόσμου',
    numberOfFloors: 1,
    numberOfRooms: 3,
    numberOfBathrooms: 1,
    numberOfWC: 2,
  });

  public pdfUrl: string = 'https://pdfobject.com/pdf/sample.pdf';

  public sanitizedPdfUrl!: SafeUrl;
  public relatedOffer: Offer | undefined;

  private readonly destroyRef = inject(DestroyRef);
  activeLang: string = 'el';

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _location: Location,
    private _portalApi: PortalApiService,
    private _paymentsApiService: ServicesPaymentsApiService,
    private responsiveService: ResponsiveService,
    private modalService: ModalService,
    private authService: AuthService,
    private _translate: TranslocoService,
    private _cmsService: CmsApiService,
    @Inject(ToasterService) private _toastService: ToasterService,
    @Inject(EnvironmentInjector) private envInjector: EnvironmentInjector
  ) {
    const serviceTypeParam =
      this._activatedRoute.snapshot.paramMap.get('serviceType');
    this.serviceType =
      UnikoServiceTypes[serviceTypeParam as keyof typeof UnikoServiceTypes];
    this.getServiceData();

    this.propertyCode = this._activatedRoute.snapshot.queryParams['property'];

    this.isMobile$ = this.responsiveService.isMobile$;

    if (!this.serviceType) {
      this._location.back();
    }
  }

  ngOnInit() {
    this._translate.langChanges$.subscribe(lang => {
      this.activeLang = lang;
      this.getServiceData();
      var sku = UnikoServicesConfig.find(
        (c) => c.type === this.serviceType
      )?.sku;
      if (!sku) {
        return;
      }
      this._cmsService.offers().subscribe((offers) => {
        this.relatedOffer = offers?.find(
          (offer) => offer.sku == sku && offer.offerExpirationDate > new Date()
        );
      });
      this._paymentsApiService.getUnicoProducts().subscribe((x) => {
        var relatedProduct = x.find((p) => p.sku == sku);
        if (!relatedProduct) {
          return;
        }
        this.serviceData.serviceFee = relatedProduct.unitPrice;
      });
    });

    this._activatedRoute.paramMap.pipe(distinct()).subscribe((paramsMap) => {
      const serviceType = paramsMap.get('serviceType');

      this.serviceType =
        UnikoServiceTypes[serviceType as keyof typeof UnikoServiceTypes];
      this.getServiceData();

      var sku = UnikoServicesConfig.find(
        (c) => c.type === this.serviceType
      )?.sku;
      if (!sku) {
        return;
      }
      this._paymentsApiService.getUnicoProducts().subscribe((x) => {
        var relatedProduct = x.find((p) => p.sku == sku);
        if (!relatedProduct) {
          return;
        }
        this.serviceData.serviceFee = relatedProduct.unitPrice;
      });

      this._cmsService.offers().subscribe((offers) => {
        this.relatedOffer = offers?.find(
          (offer) => offer.sku == sku && offer.offerExpirationDate > new Date()
        );
      });

      if (this.serviceData.reportSampleDocumentName) {
        this._cmsService
          .getDocumentTemplate(this.serviceData.reportSampleDocumentName)
          .pipe(
            tap((response) => {
              const contentType = response.headers?.['content-type'];
              const blob = new Blob([response.data], { type: contentType });
              this.serviceData.reportSampleImageUrl = URL.createObjectURL(blob);
            })
          )
          .subscribe();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.serviceData.reportSampleImageUrl) {
      URL.revokeObjectURL(this.serviceData.reportSampleImageUrl);
    }
  }

  getServiceData() {
    switch (this.serviceType) {
      case UnikoServiceTypes.preapproval:
        this.serviceData = { ...preApprovalService(this.activeLang) };
        break;
      case UnikoServiceTypes.legalReport:
        this.serviceData = { ...legalReportService(this.envInjector) };
        break;
      case UnikoServiceTypes.technicalReport:
        this.serviceData = { ...technicalReportService(this.envInjector) };
        break;
      case UnikoServiceTypes.photography:
        this.serviceData = { ...photographyService };
        break;
      case UnikoServiceTypes.propertyFileConstitution:
        this.serviceData = { ...propertyFileConstitutionService };
        break;
      case UnikoServiceTypes.biddingSupport:
        this.serviceData = { ...biddingSupportService };
        break;
      default:
        break;
    }
  }

  reload(serviceType: string) {
    const serviceTypeKey = serviceType as keyof typeof UnikoServiceTypes;
    if (serviceTypeKey in UnikoServiceTypes) {
      this.serviceType = UnikoServiceTypes[serviceTypeKey];
    }
    const service =
      UnikoServiceTypesString[serviceType as keyof typeof UnikoServiceTypes];
    if (serviceType.toString() == service.toString()) {
      this.scrollToTop();
    }
    this._router.navigate(
      [localizeRoute(`/services/${serviceType}`, this.envInjector)],
      {
        queryParams: {
          property: this.propertyCode,
        },
        queryParamsHandling: 'merge',
      }
    );
  }

  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  public openFilePreview(imageUrl: string) {
    this.modalService.show(ReportTemplatePreviewModal, {
      keyboard: true,
      class: 'h-dvh md:h-auto',
      initialState: {
        imageUrl: imageUrl,
      },
    });
  }

  public toggleAside() {
    this.isOpen = !this.isOpen;
  }

  private biddingSupportIsValid(property: PropertyDetails): boolean {
    if (this.serviceType !== UnikoServiceTypes.biddingSupport) return true;

    if (
      !property.auctionRounds ||
      property.auctionRounds.rounds?.length === 0
    ) {
      this.showErrorToast(
        this._translate.translate(
          'This property is not eligible for this service.'
        ),
        this._translate.translate(
          'To use this service, the property must currently be listed for auction.'
        )
      );
      return false;
    }

    const activeRounds = property.auctionRounds.rounds?.filter(
      (x) => x.roundDate && new Date(x.roundDate) > new Date()
    );

    if (!activeRounds || activeRounds.length === 0) {
      this.showErrorToast(
        this._translate.translate(
          'This property is not eligible for this service.'
        ),
        this._translate.translate(
          'To use this service, the property must have an active auction round.'
        )
      );
      return false;
    }

    const soonestActiveRound = activeRounds.reduce((earliest, current) => {
      const earliestDate = new Date(earliest.roundDate!);
      const currentDate = new Date(current.roundDate!);
      return currentDate < earliestDate ? current : earliest;
    });

    const soonestAuctionDate = new Date(soonestActiveRound.roundDate!);
    const diffInDays =
      (soonestAuctionDate.getTime() - new Date().getTime()) /
      (1000 * 60 * 60 * 24);

    if (diffInDays <= 10) {
      this.showErrorToast(
        this._translate.translate(
          'This property is not eligible for this service.'
        ),
        this._translate.translate(
          'To use this service, the next auction round is taking place in less than 10 days.'
        )
      );
      return false;
    }

    return true;
  }

  private showErrorToast(title: string, message: string): void {
    this._toastService.showBanner(ToastType.Error, title, message);
  }

  cleanUrl(url: string): string {
    return url.replace(/^\/?(en|el)\//, '/');
  }
  protected requestSelectedService() {
    ///check if is authed
    if (this.authService.getCurrentUser() === null) {
      let redirectUrl = `${this._router.url}`;
      this.modalService.show(ServiceRequestUnauthorizedModalComponent, {
        initialState: {
          redirectUrl: localizeRoute(
            this.cleanUrl(redirectUrl),
            this.envInjector
          ),
        },
      });
      return;
    }

    if (!this.propertyCode) {
      this.navigateToPropertySelect();
      return;
    }

    this._portalApi
      .getPropertyById(this.propertyCode)
      .pipe(
        catchError((err: any) => {
          let errorMessage =
            err.status == 404
              ? this._translate.translate(
                'home.services.toasts.propertyNotPublished'
              )
              : err.message || 'Unknown error';
          this._toastService.showBanner(
            ToastType.Error,
            this._translate.translate(
              'home.services.toasts.failedToGetProperty'
            ),
            errorMessage,
            5000
          );

          return EMPTY;
        }),
        tap((res: PropertyDetails) => {
          const propertyId = res?.id;
          const sku = UnikoServicesConfig.find(
            (f) => f.type == this.serviceType
          )?.sku;

          if (sku && propertyId) {
            let orderItems: InsertOrderItemRequest[] = [];

            orderItems.push(
              new InsertOrderItemRequest({
                sku: sku,
                quantity: 1,
                propertyId: propertyId,
              })
            );

            let body = new InsertOrderRequest({
              currency: 'EUR',
              orderItems: orderItems,
            });

            if (!this.biddingSupportIsValid(res)) {
              return;
            }

            this._paymentsApiService
              .createMyOrder(body)
              .pipe(
                catchError((err: any) => {
                  this._toastService.showBanner(
                    ToastType.Error,
                    this._translate.translate('Failed to create the order'),
                    err?.detail ?? 'Unknown error',
                    5000
                  );

                  return EMPTY;
                }),
                tap((response: OrderResponse) => {
                  if (response && response.number) {
                    this._router.navigate([`/checkout/${response.number}`]);
                  }
                })
              )
              .subscribe();
          } else {
            console.error('sku or property id not found');
          }
        })
      )
      .subscribe();
  }

  generalCallback() {
    const generalCallback = this.modalService.show(GeneralCallbackModal, {
      keyboard: true,
      class: 'h-dvh md:h-auto',
    });
  }

  protected navigateToPropertySelect(): void {
    const serviceTypeString = UnikoServiceTypes[this.serviceType];
    const url = `/services/${serviceTypeString}/select-property`;
    this._router.navigate([
      localizeRoute(
        `/services/${serviceTypeString}/select-property`,
        this.envInjector
      ),
    ]);
  }

  protected backToAllServices(): void {
    this._router.navigate([localizeRoute(`/services`, this.envInjector)], {
      queryParams: {
        property: this.propertyCode,
      },
      queryParamsHandling: 'merge',
    });
  }

  private isReportType(type: UnikoServiceTypes): boolean {
    return (
      type === UnikoServiceTypes.legalReport ||
      type === UnikoServiceTypes.technicalReport
    );
  }

  protected openVrModalComponent() {
    const vrModal = this.modalService.show(ThreedViewerComponent, {
      animated: true,
      initialState: {
        SpaceId: 'KYoLdw4ygkd',
      },
      keyboard: true,
      class: 'h-dvh md:h-auto',
    });
    vrModal.onHidden?.subscribe((response: any) => { });
  }
}
